import type { MaskShapeKind, MaskVertex, SurfaceMask } from '../types.js';

// =============================================================================
// Calibration v2 — 도형 프리셋 생성기 (PLAN §3 "도형 프리셋 생성 규칙").
// 모든 좌표는 destination 정규화 [0,1] 공간. DOM 비의존 순수 함수.
// =============================================================================

/** 표준 원 근사용 큐빅 베지어 핸들 계수 (4-arc circle approximation). */
export const CIRCLE_BEZIER_K = 0.5522847498;

/** 별 프리셋 외접 반경 (중심 0.5,0.5 기준 — [0,1] 박스에 꽉 참). */
export const STAR_OUTER_RADIUS = 0.5;
/** 별 프리셋 내접 반경. */
export const STAR_INNER_RADIUS = 0.19;

/**
 * kind별 초기 마스크 도형을 생성한다. feather 0 / invert false로 시작.
 * 'custom'은 빈 캔버스에서 그리기 시작하는 용도라 rect와 동일한 정점으로
 * 출발한다 (kind만 'custom').
 */
export function createMaskShape(kind: MaskShapeKind): SurfaceMask {
  return {
    kind,
    vertices: shapeVertices(kind),
    feather: 0,
    invert: false,
  };
}

function shapeVertices(kind: MaskShapeKind): MaskVertex[] {
  switch (kind) {
    case 'rect':
    case 'custom':
      return rectVertices();
    case 'diamond':
      return diamondVertices();
    case 'star':
      return starVertices();
    case 'circle':
      return circleVertices();
  }
}

/** 4점, [0,1] 박스에 꽉 참. TL→TR→BR→BL (시계방향). */
function rectVertices(): MaskVertex[] {
  return [
    { p: { x: 0, y: 0 } },
    { p: { x: 1, y: 0 } },
    { p: { x: 1, y: 1 } },
    { p: { x: 0, y: 1 } },
  ];
}

/** 4점 — 각 변의 중점. 상→우→하→좌 (시계방향). */
function diamondVertices(): MaskVertex[] {
  return [
    { p: { x: 0.5, y: 0 } },
    { p: { x: 1, y: 0.5 } },
    { p: { x: 0.5, y: 1 } },
    { p: { x: 0, y: 0.5 } },
  ];
}

/**
 * 10점 5각 별. 중심 (0.5,0.5), 외접 반경 {@link STAR_OUTER_RADIUS},
 * 내접 반경 {@link STAR_INNER_RADIUS}. 위쪽 꼭짓점(12시)부터 시계방향으로
 * 외접·내접 정점을 번갈아 배치 (화면 좌표 y-down 기준 시계방향).
 */
function starVertices(): MaskVertex[] {
  const cx = 0.5;
  const cy = 0.5;
  const vertices: MaskVertex[] = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? STAR_OUTER_RADIUS : STAR_INNER_RADIUS;
    // -90° = 위쪽. y-down 화면 공간에서 각도 증가 = 시계방향.
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    vertices.push({
      p: { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) },
    });
  }
  return vertices;
}

/**
 * 4정점 + 큐빅 베지어 핸들의 원 근사 (k = {@link CIRCLE_BEZIER_K}).
 * 중심 (0.5,0.5), 반경 0.5. 상→우→하→좌 (시계방향).
 * h1 = 들어오는 핸들, h2 = 나가는 핸들 — 각 엣지 i→j는 (i.h2, j.h1, j.p)의
 * 큐빅 베지어가 된다.
 */
function circleVertices(): MaskVertex[] {
  const r = 0.5;
  const c = 0.5;
  const k = CIRCLE_BEZIER_K * r;
  return [
    // 12시
    { p: { x: c, y: c - r }, h1: { x: c - k, y: c - r }, h2: { x: c + k, y: c - r } },
    // 3시
    { p: { x: c + r, y: c }, h1: { x: c + r, y: c - k }, h2: { x: c + r, y: c + k } },
    // 6시
    { p: { x: c, y: c + r }, h1: { x: c + k, y: c + r }, h2: { x: c - k, y: c + r } },
    // 9시
    { p: { x: c - r, y: c }, h1: { x: c - r, y: c + k }, h2: { x: c - r, y: c - k } },
  ];
}
